import * as Yup from 'yup';
import VALIDATION_MESSAGES from 'constants/validationMessages';
import {
  minValue,
  spotRateMaxValue,
  spotNumberMaxValue,
  spotNumberMaxLength,
  cpmRateMaxValue,
} from 'constants/fieldsLimits';

const CPM_MULTIPLIER = 1000;
const EPSILON = 0.005;

const objectCpmTest = (
  path: 'impressions' | 'grossRate' | 'netRate',
  message = 'Rates and Impressions must align with this CPM Rate'
) => ({
  name: `cpm-mismatch-${path}`,
  message,
  test(this: Yup.TestContext, _value: any) {
    const { impressions, grossRate, cpmRate } =
      this.parent as Record<string, any>;
    if (
      impressions == null ||
      grossRate == null ||
      cpmRate == null ||
      impressions <= 0 ||
      grossRate < 0 ||
      cpmRate < 0
    ) {
      return true;
    }
    const expected = (grossRate / impressions) * CPM_MULTIPLIER;
    return Math.abs(expected - cpmRate) <= EPSILON;
  },
});

export const podcastValidationSchema = Yup.object({
  outletId: Yup.number().required(VALIDATION_MESSAGES.REQUIRED),
  productId: Yup.number().required(VALIDATION_MESSAGES.REQUIRED),
  creativeId: Yup.number().optional(),
  codeId: Yup.number().optional(),
  startDate: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  endDate: Yup.string()
    .optional()
    .nullable()
    .test(
      'not earlier than the start date',
      VALIDATION_MESSAGES.DATE,
      (value, context) => {
        if (value && context.parent.startDate) {
          return value >= context.parent.startDate;
        }
        return true;
      }
    ),
  impressions: Yup.number()
    .required(VALIDATION_MESSAGES.REQUIRED)
    .typeError(VALIDATION_MESSAGES.NUMBER)
    .integer(VALIDATION_MESSAGES.INTEGER)
    .min(minValue, VALIDATION_MESSAGES.POSITIVE)
    .max(
      spotNumberMaxValue,
      VALIDATION_MESSAGES.STRING_MAX_LENGTH(spotNumberMaxLength)
    )
  ,
  grossRate: Yup.number()
    .required(VALIDATION_MESSAGES.REQUIRED)
    .typeError(VALIDATION_MESSAGES.NUMBER)
    .min(minValue, VALIDATION_MESSAGES.POSITIVE)
    .max(
      spotRateMaxValue,
      VALIDATION_MESSAGES.FRACTIONAL_NUMBER_MAX_LENGTH('7', '2')
    )
    .test(
      'is valid',
      VALIDATION_MESSAGES.FRACTIONAL_NUMBER_MAX_LENGTH('7', '2'),
      (value: number | undefined) =>
        /^[0-9]{0,7}[.]{0,1}[0-9]{0,2}$/.test(String(value))
    )
  ,
  netRate: Yup.number()
    .required(VALIDATION_MESSAGES.REQUIRED)
    .typeError(VALIDATION_MESSAGES.NUMBER)
    .min(minValue, VALIDATION_MESSAGES.POSITIVE)
    .max(
      spotRateMaxValue,
      VALIDATION_MESSAGES.FRACTIONAL_NUMBER_MAX_LENGTH('7', '2')
    )
    .test(
      'is valid',
      VALIDATION_MESSAGES.FRACTIONAL_NUMBER_MAX_LENGTH('7', '2'),
      (value: number | undefined) =>
        /^[0-9]{0,7}[.]{0,1}[0-9]{0,2}$/.test(String(value))
    )
  ,
  cpmRate: Yup.number()
    .required(VALIDATION_MESSAGES.REQUIRED)
    .typeError(VALIDATION_MESSAGES.NUMBER)
    .min(minValue, VALIDATION_MESSAGES.POSITIVE)
    .max(
      cpmRateMaxValue,
      VALIDATION_MESSAGES.FRACTIONAL_NUMBER_MAX_LENGTH('10', '2')
    )
    .test(
      'is valid',
      VALIDATION_MESSAGES.FRACTIONAL_NUMBER_MAX_LENGTH('10', '2'),
      (value: number | undefined) =>
        /^[0-9]{0,10}[.]{0,1}[0-9]{0,2}$/.test(String(value))
    )
    .test(objectCpmTest('impressions'))
    .test(objectCpmTest('grossRate'))
    .test(objectCpmTest('netRate')),
  trafficSentAtDate: Yup.string().optional().nullable(),
  isTestShow: Yup.boolean().required(VALIDATION_MESSAGES.REQUIRED),
  includesVideoImpressions: Yup.boolean().required(VALIDATION_MESSAGES.REQUIRED),
  isDigitallyInserted: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  isPixelRequired: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  read: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  endorsement: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  position: Yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  bonusType: Yup.string().oneOf(['AV', 'AVP', 'MGE', 'MGU', '']).nullable(),
  originSpotIds: Yup.array()
    .of(Yup.number().integer())
    .when('bonusType', {
      is: (v: string) => v === 'MGE' || v === 'MGU',
      then: (schema) =>
        schema.min(1, 'At least one origin spot is required for MGE/MGU bonus types'),
      otherwise: (schema) => schema.notRequired(),
    }),
});
